// Thực thể HS · Hải quan (O-DT, issue 1291): JSON của /v1/hs và /v1/hs/customs → kiểu cho màn Tư vấn HS / Số liệu hải quan.
// Số liệu hải quan chỉ lượng / HS / nước — KHÔNG tiền, KHÔNG liên hệ (khớp SQL 017).
import type { ApiRoute } from './api';

/** Một mã HS 6 số + tên 3 cấp (chương · nhóm · phân nhóm). */
export interface HsCode {
  id: string;
  code: string;
  name: string;
  group: string;
  chapter: string;
}

/** Một dòng số liệu tờ khai thị trường. */
export interface HsCustoms {
  id: string;
  year: number | null;
  month: number | null;
  date: string;
  taxId: string;
  shipper: string;
  consignee: string;
  hs: string;
  commodity: string;
  unit: string;
  qty: number | null;
  paymentTerm: string;
  exportTerm: string;
  customs: string;
  type: string;
  from: string;
  to: string;
  declNo: string;
  mode: string;
  shipments: number | null;
}

declare module './source' {
  interface Entities {
    hsCode: HsCode;
    hsCustoms: HsCustoms;
  }
}

type Row = Record<string, unknown>;
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '');
const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

export function hsCodeFromApi(r: Row): HsCode {
  return {
    id: str(r.Id),
    code: str(r.OData__x0048_S6),
    name: str(r.HS6Name),
    group: str(r.HS4Name),
    chapter: str(r.HS2Name),
  };
}

export function hsCustomsFromApi(r: Row): HsCustoms {
  return {
    id: str(r.Id),
    year: num(r.Year),
    month: num(r.Month),
    date: str(r.ExportDate).slice(0, 10),
    taxId: str(r.TaxID),
    shipper: str(r.Shipper),
    consignee: str(r.Consignee),
    hs: str(r.HSCode),
    commodity: str(r.Commodity),
    unit: str(r.Unit),
    qty: num(r.Quantiy),
    paymentTerm: str(r.PaymentTerm),
    exportTerm: str(r.ExportTerm),
    customs: str(r.Customs),
    type: str(r.ExportType),
    from: str(r.ExportCountry),
    to: str(r.ImportCountry),
    declNo: str(r.CustomsNo),
    mode: str(r.TransportMode),
    shipments: num(r.Shipments),
  };
}

const rows = (json: unknown): Row[] => {
  const list = (json as { items?: unknown } | null)?.items;
  return Array.isArray(list) ? (list as Row[]) : [];
};
const hasMore = (json: unknown) => (json as { hasMore?: boolean } | null)?.hasMore === true;
const paging = (q: { offset?: number; limit?: number }) => {
  const limit = q.limit && q.limit > 0 ? q.limit : 50;
  return { limit, page: Math.floor((q.offset ?? 0) / limit) + 1 };
};

export const hsCodeRoute: ApiRoute<HsCode> = {
  path: '/api/v1/hs',
  query: (q) => ({ q: q.q?.trim(), code: typeof q.where?.code === 'string' ? q.where.code : undefined, ...paging(q) }),
  parse: (json) => ({ items: rows(json).map(hsCodeFromApi), hasMore: hasMore(json) }),
};

export const hsCustomsRoute: ApiRoute<HsCustoms> = {
  path: '/api/v1/hs/customs',
  query: (q) => {
    const w = q.where ?? {};
    const pick = (k: string) => (w[k] === undefined ? undefined : String(w[k]));
    return { q: q.q?.trim(), hs: pick('hs'), year: pick('year'), month: pick('month'), country: pick('country'), mode: pick('mode'), ...paging(q) };
  },
  parse: (json) => ({ items: rows(json).map(hsCustomsFromApi), hasMore: hasMore(json) }),
};
